import type { FC, ReactNode } from 'react'

import { useEffect } from 'react'
import { useRouter } from 'next/router'
import Lenis from 'lenis'

import { gsap, ScrollTrigger } from '@/lib/gsap'

let lenisInstance: Lenis | null = null

export const lockScroll = (locked: boolean): void => {
    if (lenisInstance) {
        if (locked) lenisInstance.stop()
        else lenisInstance.start()
    }

    if (typeof document !== 'undefined') {
        document.body.style.overflow = locked ? 'hidden' : ''
    }
}

export const smoothScrollTo = (target: string | number): void => {
    if (lenisInstance) {
        lenisInstance.scrollTo(target, { offset: -84, duration: 1.2 })
        return
    }

    if (typeof target === 'number') {
        window.scrollTo({ top: target, behavior: 'smooth' })
    } else {
        document.querySelector(target)?.scrollIntoView({ behavior: 'smooth' })
    }
}

export const SmoothScroll: FC<{ children: ReactNode }> = ({ children }) => {
    const router = useRouter()

    useEffect(() => {
        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const isTouch = window.matchMedia('(pointer: coarse)').matches
        // On touch devices, native scrolling (esp. iOS Safari) is smoother than
        // JS-driven smooth scroll — running Lenis there causes jank and flicker.
        if (prefersReduced || isTouch) return

        const lenis = new Lenis({
            duration: 1.05,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
            wheelMultiplier: 1,
            touchMultiplier: 1.6
        })
        lenisInstance = lenis

        lenis.on('scroll', ScrollTrigger.update)

        const raf = (time: number) => {
            lenis.raf(time * 1000)
        }

        gsap.ticker.add(raf)
        gsap.ticker.lagSmoothing(0)

        return () => {
            gsap.ticker.remove(raf)
            lenis.destroy()
            lenisInstance = null
        }
    }, [])

    useEffect(() => {
        let timer = 0
        const scrollToHash = (url: string) => {
            // Links such as `/kurse#id` (search results) land on that item once the page has settled.
            const hashIndex = url.indexOf('#')
            const id = hashIndex >= 0 ? decodeURIComponent(url.slice(hashIndex + 1)) : ''
            window.clearTimeout(timer)
            if (id) {
                timer = window.setTimeout(() => {
                    if (document.getElementById(id)) smoothScrollTo(`#${CSS.escape(id)}`)
                }, 450)
            }
        }

        const onRouteChange = (url: string) => {
            if (lenisInstance) lenisInstance.scrollTo(0, { immediate: true })
            else window.scrollTo(0, 0)
            requestAnimationFrame(() => ScrollTrigger.refresh())
            scrollToHash(url)
        }

        router.events.on('routeChangeComplete', onRouteChange)
        router.events.on('hashChangeComplete', scrollToHash)
        return () => {
            window.clearTimeout(timer)
            router.events.off('routeChangeComplete', onRouteChange)
            router.events.off('hashChangeComplete', scrollToHash)
        }
    }, [router.events])

    // Shared links such as `/audiolibra#slug` land on that item once the page has settled.
    useEffect(() => {
        const id = decodeURIComponent(window.location.hash.slice(1))
        if (!id) return

        const timer = window.setTimeout(() => {
            if (document.getElementById(id)) smoothScrollTo(`#${CSS.escape(id)}`)
        }, 450)

        return () => window.clearTimeout(timer)
    }, [])

    useEffect(() => {
        const refresh = () => ScrollTrigger.refresh()

        if (typeof document !== 'undefined' && 'fonts' in document) {
            document.fonts.ready.then(refresh)
        }

        window.addEventListener('load', refresh)
        return () => window.removeEventListener('load', refresh)
    }, [])

    return <>{children}</>
}
